import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from '../lib/AuthContext';
import { getMultiplier, getMaxGems, MULTIS, getProbability, generateSeed, generateMinesFromSeed, sha256, generateClientSeed } from '../lib/multis';
import supabase from '../lib/supabase';
import Chat from '../components/Chat';

// ── SVGs ──────────────────────────────────────────────────────────────────────
const GEM_SVG = (
  <svg viewBox="0 0 100 100" style={{ width:'52%', height:'52%', position:'absolute' }}>
    <polygon points="50,5 95,35 95,65 50,95 5,65 5,35" fill="#fcb10f" opacity="0.9"/>
    <polygon points="50,5 95,35 50,55" fill="#ffd966" opacity="0.7"/>
    <polygon points="50,95 5,65 50,55" fill="#e69900" opacity="0.8"/>
  </svg>
);

const BOMB_SVG = (
  <svg viewBox="0 0 100 100" style={{ width:'52%', height:'52%', position:'absolute' }}>
    <circle cx="50" cy="55" r="32" fill="#cc2200"/>
    <circle cx="50" cy="55" r="22" fill="#ff3311"/>
    <rect x="46" y="18" width="8" height="16" rx="4" fill="#888"/>
    <circle cx="62" cy="20" r="5" fill="#ffcc00"/>
    <line x1="54" y1="20" x2="62" y2="20" stroke="#888" strokeWidth="3"/>
    <circle cx="38" cy="45" r="6" fill="rgba(255,255,255,0.3)"/>
  </svg>
);

const IDLE_GEM = (
  <svg viewBox="0 0 100 100" style={{ width:'52%', height:'52%', position:'absolute', opacity:0.12 }}>
    <polygon points="50,5 95,35 95,65 50,95 5,65 5,35" fill="#fcb10f"/>
  </svg>
);

// ── Sounds ────────────────────────────────────────────────────────────────────
function playSound(url) {
  try { 
    const audio = new Audio(url);
    audio.volume = 0.3;
    audio.play().catch(() => {});
  } catch(e) {}
}
const SND_GEM     = 'https://aefobtuktdlrblkoummu.supabase.co/storage/v1/object/public/sounds/gem.mp3';
const SND_MINE    = 'https://aefobtuktdlrblkoummu.supabase.co/storage/v1/object/public/sounds/mine.mp3';
const SND_CASHOUT = 'https://aefobtuktdlrblkoummu.supabase.co/storage/v1/object/public/sounds/success.mp3';

// ── Mine placement ────────────────────────────────────────────────────────────
function placeMines(count) {
  const positions = Array.from({ length: 25 }, (_, i) => i);
  for (let i = positions.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [positions[i], positions[j]] = [positions[j], positions[i]];
  }
  return new Set(positions.slice(0, count));
}

// ── Styles ────────────────────────────────────────────────────────────────────
const css = `
  .tile { aspect-ratio:1; border-radius:8px; border:2px solid #202329; background:#121418; cursor:pointer; position:relative; overflow:hidden; display:flex; align-items:center; justify-content:center; transition:background 200ms, transform 200ms, border-color 200ms; }
  .tile:hover:not(:disabled):not(.gem-tile):not(.mine-tile) { border-color:#3a3d45; transform:scale(1.04); background:#1a1d24; }
  .tile:disabled { cursor:default; }
  .tile.idle {}
  .tile.gem-tile { background:rgba(255,189,51,0.133); border-color:#fcb10f; transform:scale(1.05); }
  .tile.mine-tile { background:rgba(181,43,25,0.267); border-color:#ff2020; box-shadow:0 0 15px -2px #b52b19; transform:scale(1.05); }
  .tile.faded { opacity:0.45; }
  @keyframes popIn { 0%{transform:scale(0.7)} 60%{transform:scale(1.12)} 100%{transform:scale(1.05)} }
  .tile.gem-tile { animation: popIn 0.25s ease forwards; }
`;

const S = {
  page: { minHeight:'calc(100vh - 56px)', background:'#0f1115', display:'flex', alignItems:'flex-start', justifyContent:'center', padding:'20px 16px' },
  container: { display:'flex', gap:'14px', width:'100%', maxWidth:'1200px', alignItems:'flex-start' },

  // LEFT PANEL
  left: { background:'#1a1d24', borderRadius:'12px', padding:'16px', width:'220px', flexShrink:0 },
  label: { fontSize:'11px', color:'#6b7280', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'6px' },
  betWrap: { background:'#0f1115', border:'1px solid #2a2d35', borderRadius:'8px', display:'flex', alignItems:'center', padding:'0 10px', height:'40px', marginBottom:'8px' },
  betSymbol: { fontSize:'13px', color:'#6b7280', marginRight:'6px' },
  betInput: { background:'none', border:'none', outline:'none', color:'#fff', fontSize:'14px', width:'100%' },
  quickBtns: { display:'flex', gap:'4px', marginBottom:'12px' },
  quickBtn: { flex:1, background:'#0f1115', border:'1px solid #2a2d35', borderRadius:'6px', color:'#9ca3af', fontSize:'11px', fontWeight:600, padding:'6px 0', cursor:'pointer', transition:'all 0.15s' },
  quickBtnHover: { borderColor:'#00e701', color:'#00e701' },
  select: { width:'100%', background:'#0f1115', border:'1px solid #2a2d35', borderRadius:'8px', color:'#fff', fontSize:'14px', padding:'10px', outline:'none', marginBottom:'12px', cursor:'pointer' },
  btnBet: { width:'100%', background:'#00e701', color:'#000', fontWeight:700, fontSize:'14px', border:'none', borderRadius:'8px', height:'44px', cursor:'pointer', transition:'filter 0.15s', marginBottom:'8px' },
  btnBetDisabled: { background:'#2a5e2b', color:'#4a8a4b', cursor:'not-allowed' },
  btnCashout: { width:'100%', background:'#00e701', color:'#000', fontWeight:700, fontSize:'14px', border:'none', borderRadius:'8px', height:'44px', cursor:'pointer', transition:'filter 0.15s', marginBottom:'8px' },
  divider: { height:'1px', background:'#2a2d35', margin:'12px 0' },
  statRow: { display:'flex', justifyContent:'space-between', fontSize:'12px', marginBottom:'8px' },
  statLbl: { color:'#6b7280' },
  statVal: { color:'#fff', fontWeight:500 },
  statGreen: { color:'#00e701', fontWeight:500 },

  // GRID
  gameWrap: { flex:'1 0 400px', position:'relative' },
  grid: { display:'grid', gridTemplateColumns:'repeat(5, 1fr)', gap:'8px', width:'100%' },

  // OVERLAY
  overlay: { position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', borderRadius:'8px', zIndex:10, pointerEvents:'none', opacity:0, transition:'opacity 0.3s ease' },
  overlayShow: { opacity:1, pointerEvents:'auto' },
  overlayCard: { background:'#1a1d24', border:'1px solid #2a2d35', borderRadius:'12px', padding:'20px 28px', textAlign:'center', cursor:'pointer', transition:'transform 0.15s' },
  overlayCardLoss: { borderColor:'#ff2020', background:'rgba(181,43,25,0.2)' },
  overlayMulti: { fontSize:'32px', fontWeight:700, color:'#00e701', marginBottom:'8px' },
  overlayMultiLoss: { color:'#ff4444' },
  overlayPayout: { display:'flex', gap:'16px', justifyContent:'center', fontSize:'14px', color:'#9ca3af' },

  // RIGHT PANEL
  right: { background:'#1a1d24', borderRadius:'12px', padding:'16px', width:'180px', flexShrink:0, alignSelf:'flex-start' },
  probTitle: { fontSize:'11px', color:'#6b7280', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'10px' },
  probRow: { display:'flex', justifyContent:'space-between', alignItems:'center', fontSize:'12px', marginBottom:'6px', padding:'5px 8px', borderRadius:'6px', background:'#0f1115' },
  probRowCurrent: { border:'1px solid #fcb10f' },
  probRowDone: { opacity:0.4 },
  probGem: { color:'#9ca3af', display:'flex', alignItems:'center', gap:'5px' },
  probPct: { fontWeight:600, color:'#fff' },
  probPctCurrent: { color:'#fcb10f' },
  probPctSafe: { color:'#00e701' },
  probNote: { fontSize:'10px', color:'#4b5563', marginTop:'10px', lineHeight:1.5 },
};

// ── MULTIPLIER PANEL ────────────────────────────────────────────
function MultiPanel({ mineCount, gemsRevealed, active }) {
  const maxGems = getMaxGems(mineCount);
  const multis = MULTIS[mineCount] || [];

  const windowSize = 10;
  let startIdx = Math.max(0, gemsRevealed - Math.floor(windowSize / 2));
  startIdx = Math.min(startIdx, Math.max(0, maxGems - windowSize));
  const rows = [];
  for (let g = startIdx; g < Math.min(startIdx + windowSize, maxGems); g++) {
    const multi = multis[g] != null ? multis[g].toFixed(2) + 'x' : '-';
    const isDone = g < gemsRevealed;
    const isCurrent = g === gemsRevealed && active;
    rows.push({ g, multi, isDone, isCurrent });
  }

  return (
    <div style={S.right}>
      <div style={S.probTitle}>Multipliers</div>
      {rows.map(({ g, multi, isDone, isCurrent }) => (
        <div key={g} style={{
          ...S.probRow,
          ...(isCurrent ? S.probRowCurrent : {}),
          ...(isDone ? S.probRowDone : {}),
        }}>
          <span style={S.probGem}>
            <svg viewBox="0 0 20 20" width="12" height="12">
              <circle cx="10" cy="10" r="8" fill={isDone ? '#fcb10f' : '#4b5563'}/>
            </svg>
            {g + 1}
          </span>
          <span style={{
            ...S.probPct,
            ...(isCurrent ? S.probPctCurrent : {}),
            ...(!isDone && !isCurrent ? S.probPctSafe : {}),
          }}>{multi}</span>
        </div>
      ))}
      <div style={S.probNote}>Payout multiplier for each gem found.</div>
    </div>
  );
}

// ── MAIN GAME COMPONENT ───────────────────────────────────────────────────────
export default function Game() {
  const { profile, refreshProfile } = useAuth();

  const [bet, setBet] = useState('1');
  const [mineCount, setMineCount] = useState(3);

  // Game state
  const [active, setActive] = useState(false);
  const [mines, setMines] = useState(new Set());
  const [revealed, setRevealed] = useState(new Array(25).fill(null)); // null | 'gem' | 'mine'
  const [gemsRevealed, setGemsRevealed] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [won, setWon] = useState(false);
  const [currentBet, setCurrentBet] = useState(0);
  const [saving, setSaving] = useState(false);
  const [serverSeed, setServerSeed] = useState('');
  const [clientSeed, setClientSeed] = useState('');
  const [nonce, setNonce] = useState(0);
  const [currentNonce, setCurrentNonce] = useState(0);
  const [showSeeds, setShowSeeds] = useState(false);
  const [seedHash, setSeedHash] = useState('');

  const balance = Number(profile?.balance || 0);
  const multiplier = active || gameOver ? getMultiplier(mineCount, gemsRevealed) : 1;
  const profit = gemsRevealed > 0 ? (currentBet * multiplier - currentBet) : 0;
  const nextPct = active ? getProbability(mineCount, gemsRevealed).toFixed(1) + '% safe' : '-';
  const maxGems = getMaxGems(mineCount);

  // Initialize seeds on mount
  useEffect(() => {
    if (profile?.id) {
      loadSeeds();
    }
  }, [profile]);

  async function loadSeeds() {
    const storedServerSeed = localStorage.getItem('mines_server_seed');
    const storedClientSeed = localStorage.getItem('mines_client_seed');
    const storedNonce = parseInt(localStorage.getItem('mines_nonce') || '0');
    
    if (!storedServerSeed) {
      const newSeed = await generateSeed();
      const hash = await sha256(newSeed);
      setServerSeed(newSeed);
      setSeedHash(hash);
      localStorage.setItem('mines_server_seed', newSeed);
      localStorage.setItem('mines_seed_hash', hash);
    } else {
      setServerSeed(storedServerSeed);
      setSeedHash(localStorage.getItem('mines_seed_hash') || '');
    }
    
    if (!storedClientSeed) {
      const newClientSeed = generateClientSeed();
      setClientSeed(newClientSeed);
      localStorage.setItem('mines_client_seed', newClientSeed);
    } else {
      setClientSeed(storedClientSeed);
    }
    
    setNonce(storedNonce);
  }

  async function regenerateSeeds() {
    const newSeed = await generateSeed();
    const hash = await sha256(newSeed);
    setServerSeed(newSeed);
    setSeedHash(hash);
    setNonce(0);
    localStorage.setItem('mines_server_seed', newSeed);
    localStorage.setItem('mines_seed_hash', hash);
    localStorage.setItem('mines_nonce', '0');
  }

  function changeClientSeed() {
    const newSeed = generateClientSeed();
    setClientSeed(newSeed);
    localStorage.setItem('mines_client_seed', newSeed);
  }

  // Save game result to Supabase and update balance
  const saveResult = useCallback(async (didWin, gems, finalBet, finalMult, gameNonce) => {
    if (!profile) return;
    setSaving(true);
    try {
      const payout = didWin ? finalBet * finalMult : 0;
      const newBalance = balance - finalBet + payout;

      await supabase.from('users').update({ balance: newBalance }).eq('id', profile.id);
      await supabase.from('games').insert([{
        user_id: profile.id,
        bet_amount: finalBet,
        mine_count: mineCount,
        gems_revealed: gems,
        payout: payout,
        multiplier: finalMult,
        won: didWin,
        house_edge_taken: didWin ? (finalBet * finalMult * 0.07) : finalBet,
        seed: serverSeed,
        client_seed: clientSeed,
        nonce: gameNonce,
      }]);
      await refreshProfile();
    } catch (e) {
      console.error('saveResult error', e);
    } finally {
      setSaving(false);
    }
  }, [profile, balance, mineCount, refreshProfile, serverSeed, clientSeed]);

  const startGame = useCallback(async () => {
    const betAmt = parseInt(bet) || 0;
    const currentBalance = Number(balance) || 0;
    if (betAmt <= 0) return;
    if (betAmt > currentBalance) return;

    const newNonce = nonce + 1;
    setNonce(newNonce);
    setCurrentNonce(newNonce);
    localStorage.setItem('mines_nonce', newNonce.toString());

    const gameMines = await generateMinesFromSeed(serverSeed, clientSeed, newNonce, mineCount);

    setCurrentBet(betAmt);
    setActive(true);
    setGameOver(false);
    setWon(false);
    setRevealed(new Array(25).fill(null));
    setGemsRevealed(0);
    setMines(gameMines);

    try {
      await supabase.from('users').update({ balance: currentBalance - betAmt }).eq('id', profile.id);
      await refreshProfile();
    } catch (e) {
      console.error('Failed to deduct balance:', e);
    }
  }, [bet, balance, mineCount, profile, refreshProfile]);

  const revealTile = useCallback((i) => {
    if (!active || revealed[i] !== null) return;

    const isMine = mines.has(i);
    const newRevealed = [...revealed];

    if (isMine) {
      playSound(SND_MINE);
      newRevealed[i] = 'mine';
      // reveal all other mines
      mines.forEach(m => { if (newRevealed[m] === null) newRevealed[m] = 'mine-faded'; });
      setRevealed(newRevealed);
      setActive(false);
      setGameOver(true);
      setWon(false);
      saveResult(false, gemsRevealed, currentBet, multiplier, currentNonce);
    } else {
      playSound(SND_GEM);
      newRevealed[i] = 'gem';
      const newGems = gemsRevealed + 1;
      setRevealed(newRevealed);
      setGemsRevealed(newGems);
      // Check if all gems found
      if (newGems === maxGems) {
        const finalMult = getMultiplier(mineCount, newGems);
        playSound(SND_CASHOUT);
        setActive(false);
        setGameOver(true);
        setWon(true);
        saveResult(true, newGems, currentBet, finalMult, currentNonce);
      }
    }
  }, [active, revealed, mines, gemsRevealed, currentBet, multiplier, mineCount, maxGems, saveResult, currentNonce]);

  const cashOut = useCallback(() => {
    if (!active || gemsRevealed === 0) return;
    playSound(SND_CASHOUT);
    setActive(false);
    setGameOver(true);
    setWon(true);
    saveResult(true, gemsRevealed, currentBet, multiplier, currentNonce);
  }, [active, gemsRevealed, currentBet, multiplier, saveResult, currentNonce]);

  const reset = useCallback(() => {
    setGameOver(false);
    setActive(false);
    setRevealed(new Array(25).fill(null));
    setGemsRevealed(0);
    setMines(new Set());
    setCurrentBet(0);
  }, []);

  const canBet = !active && !saving && parseInt(bet) > 0 && parseInt(bet) <= balance;

  if (!profile) {
    return (
      <div style={{ ...S.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#6b7280', fontSize: '14px' }}>Loading...</div>
      </div>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{css}</style>
      <div style={S.page}>
        <div style={S.container}>

          {/* LEFT PANEL */}
          <div style={S.left}>
            <div style={S.label}>Bet Amount</div>
            <div style={S.betWrap}>
              <span style={S.betSymbol}>₿</span>
              <input
                style={S.betInput}
                value={bet}
                onChange={e => setBet(e.target.value.replace(/[^0-9]/g, ''))}
                disabled={active}
                type="text"
                inputMode="numeric"
                placeholder="0"
              />
            </div>
            <div style={S.quickBtns}>
              <button
                style={S.quickBtn}
                onClick={() => !active && setBet(String(Math.max(1, Math.floor(parseInt(bet || 0) / 2))))}
                disabled={active}
              >
                ½
              </button>
              <button
                style={S.quickBtn}
                onClick={() => !active && setBet(String(Math.max(1, parseInt(bet || 0) * 2)))}
                disabled={active}
              >
                2×
              </button>
              <button
                style={S.quickBtn}
                onClick={() => !active && setBet(String(Math.max(1, Math.floor(balance))))}
                disabled={active}
              >
                MAX
              </button>
            </div>

            <div style={S.label}>Mines</div>
            <select
              style={S.select}
              value={mineCount}
              onChange={e => setMineCount(Number(e.target.value))}
              disabled={active}
            >
              {[1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,24].map(n => (
                <option key={n} value={n}>{n} {n === 1 ? 'mine' : 'mines'}</option>
              ))}
            </select>

            {!active ? (
              <button
                style={{ ...S.btnBet, ...(!canBet ? S.btnBetDisabled : {}) }}
                onClick={startGame}
                disabled={!canBet}
              >
                {saving ? 'Saving...' : 'Bet'}
              </button>
            ) : (
              <button
                style={{ ...S.btnCashout, ...(gemsRevealed === 0 ? { opacity: 0.5 } : {}) }}
                onClick={cashOut}
                disabled={gemsRevealed === 0}
              >
                Cash Out ₿{(currentBet * multiplier).toFixed(2)}
              </button>
            )}

            <div style={S.divider} />

            <div style={S.statRow}>
              <span style={S.statLbl}>Multiplier</span>
              <span style={S.statVal}>{multiplier.toFixed(2)}x</span>
            </div>
            <div style={S.statRow}>
              <span style={S.statLbl}>Profit</span>
              <span style={S.statGreen}>₿{profit.toFixed(2)}</span>
            </div>
            <div style={S.statRow}>
              <span style={S.statLbl}>Next tile</span>
              <span style={S.statVal}>{nextPct}</span>
            </div>
            <div style={S.statRow}>
              <span style={S.statLbl}>Gems found</span>
              <span style={S.statVal}>{gemsRevealed}</span>
            </div>
            <div style={S.statRow}>
              <span style={S.statLbl}>Balance</span>
              <span style={S.statVal}>₿{balance.toFixed(2)}</span>
            </div>

            <div style={S.divider} />

            <button
              style={{ ...S.quickBtn, fontSize: '10px', padding: '8px 4px' }}
              onClick={() => setShowSeeds(!showSeeds)}
            >
              {showSeeds ? 'Hide' : 'Fairness'}
            </button>

            {showSeeds && serverSeed && (
              <div style={{ marginTop: '12px', fontSize: '10px', color: '#6b7280' }}>
                <div style={{ marginBottom: '8px' }}>
                  <span style={{ color: '#9ca3af' }}>Nonce:</span> {nonce}
                </div>
                <div style={{ marginBottom: '8px', wordBreak: 'break-all' }}>
                  <span style={{ color: '#9ca3af' }}>Server Seed:</span><br />
                  <span style={{ fontFamily: 'monospace', fontSize: '9px' }}>{seedHash ? '...' + seedHash.slice(-8) : 'Loading...'}</span>
                </div>
                <div style={{ marginBottom: '8px', wordBreak: 'break-all' }}>
                  <span style={{ color: '#9ca3af' }}>Client Seed:</span><br />
                  <span style={{ fontFamily: 'monospace', fontSize: '9px' }}>{clientSeed || 'Loading...'}</span>
                </div>
                <button
                  style={{ ...S.quickBtn, fontSize: '10px', padding: '6px', marginTop: '4px', width: '100%' }}
                  onClick={regenerateSeeds}
                >
                  Regenerate
                </button>
                <button
                  style={{ ...S.quickBtn, fontSize: '10px', padding: '6px', marginTop: '4px', width: '100%' }}
                  onClick={changeClientSeed}
                >
                  Change Client Seed
                </button>
              </div>
            )}
          </div>

          {/* GRID */}
          <div style={S.gameWrap}>
            <div style={S.grid}>
              {Array.from({ length: 25 }, (_, i) => {
                const state = revealed[i];
                let cls = 'tile';
                if (state === 'gem') cls += ' gem-tile';
                else if (state === 'mine') cls += ' mine-tile';
                else if (state === 'mine-faded') cls += ' mine-tile faded';
                else if (gameOver) cls += ' faded';
                else cls += ' idle';

                return (
                  <button
                    key={i}
                    className={cls}
                    disabled={!active || state !== null}
                    onClick={() => revealTile(i)}
                    style={{ position:'relative' }}
                  >
                    {state === 'gem' && GEM_SVG}
                    {(state === 'mine' || state === 'mine-faded') && BOMB_SVG}
                    {state === null && IDLE_GEM}
                  </button>
                );
              })}
            </div>

            {/* OVERLAY */}
            {gameOver && (
              <div style={{ ...S.overlay, ...S.overlayShow }}>
                <div
                  style={{ ...S.overlayCard, ...(won ? {} : S.overlayCardLoss) }}
                  onClick={reset}
                >
                  <div style={{ ...S.overlayMulti, ...(won ? {} : S.overlayMultiLoss) }}>
                    {won ? 'x' + (currentBet > 0 ? (currentBet * multiplier / currentBet).toFixed(2) : multiplier.toFixed(2)) : 'x0.00'}
                  </div>
                  <div style={S.overlayPayout}>
                    <span>
                      <svg viewBox="0 0 100 100" width="14" height="14"><circle cx="50" cy="50" r="45" fill="#b8b8b8"/></svg>
                      &nbsp;₿{won ? (currentBet * multiplier).toFixed(2) : '0.00'}
                    </span>
                    <span>
                      <svg viewBox="0 0 200 200" width="14" height="14"><polygon points="100,10 130,80 200,80 145,125 165,195 100,155 35,195 55,125 0,80 70,80" fill="#fcb10f"/></svg>
                      &nbsp;{gemsRevealed}
                    </span>
                  </div>
                  <div style={{ fontSize:'11px', color:'#6b7280', marginTop:'8px' }}>Click to play again</div>
                </div>
              </div>
            )}
          </div>

          {/* RIGHT PANEL */}
          <MultiPanel mineCount={mineCount} gemsRevealed={gemsRevealed} active={active} />
          
          {/* CHAT */}
          <Chat />
        </div>
      </div>
    </>
  );
}